<template>
  <v-dialog
    v-model="dialogToggle"
    persistent
    scrollable
  >
    <v-card
      id="berechnungsparameter-anpassen-dialog"
    >
      <v-card-title>
        <v-row>
          <v-col cols="auto">
            <h3>Szenario-Einstellungen</h3>
          </v-col>
          <v-col
            cols="auto"
            class="mr-auto"
          >
            <v-tooltip
              v-if="
                loaded &&
                  (globalCivilizationChanged || localCivilizationChanged)
              "
              right
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="resetUpdateValues"
                >
                  <v-icon>fa-undo</v-icon>
                </v-btn>
              </template>
              <span>auf Ausgangswerte zurücksetzen</span>
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <v-btn
              id="close-berechnungsparameter-anpassen-dialog"
              icon
              @click="dialogToggle = false"
            >
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <EditGlobalCivilization
          v-model="globalCivilizations"
          :global-civilizations-original="globalCivilizationsOriginal"
          :loaded="loaded"
          :changed="globalCivilizationChanged"
          :mode="mode"
          @reset="resetGlobalCivilizationValues"
        />
        <v-divider />
        <EditLocalCivilization
          v-model="localCivilizations"
          :local-civilizations-original="localCivilizationsOriginal"
          :loaded="loaded"
          :changed="localCivilizationChanged"
          :mode="mode"
          @reset="resetLocalCivilizationValues"
        />
      </v-card-text>
      <v-divider />
      <v-card-actions
        v-if="mode == 'edit'"
        class="d-flex flex-column justify-center"
      >
        <v-alert
          v-if="!saving && changesSaved"
          elevation="2"
          border="top"
          colored-border
          :type="savingError ? 'error' : 'success'"
          class="mt-3"
        >
          <template v-if="savingError">
            Folgende Änderungen konnten nicht gespeichert werden: <br>
            {{ savingError }}
          </template>
          <template v-else>
            Die Änderungen wurden erfolgreich gespeichert.
          </template>
        </v-alert>
        <v-btn
          :disabled="
            saving ||
              !loaded ||
              !(globalCivilizationChanged || localCivilizationChanged) ||
              civilizationValueError
          "
          class="my-3"
          color="primary"
          :loading="saving"
          @click="putCivilizationInput"
        >
          Änderungen speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from "vuex";
import EditGlobalCivilization from "@/components/dialogs/updateSettingsDialog/EditGlobalCivilization";
import EditLocalCivilization from "@/components/dialogs/updateSettingsDialog/EditLocalCivilization";

// TODO: error handling überarbeiten (gibt bisher rohe server messages durch)
export default {
  name: "UpdateSettingsDialog",
  components: { EditGlobalCivilization, EditLocalCivilization },
  props: {
    showDialog: Boolean,
    mode: String
  },
  data() {
    return {
      globalCivilizations: [],
      localCivilizations: [],
      // clones to keep track of original state
      globalCivilizationsOriginal: [],
      localCivilizationsOriginal: [],
      loaded: false,
      saving: false,
      changesSaved: false,
      savingError: null
    };
  },
  computed: {
    ...mapGetters(["selectedUpdate", "user"]),
    // toggle dialog visibility based on showDialog prop
    dialogToggle: {
      get() {
        return this.showDialog;
      },
      set(newVal) {
        if (!newVal) {
          // change showDialog prop in parent component
          this.$emit("close");
          this.loaded = false;
        }
      }
    },
    // whether globalCivilizations has been modified and is != globalCivilizationsOriginal
    globalCivilizationChanged() {
      return this.civilizationArrayHasChanged(
        this.globalCivilizations,
        this.globalCivilizationsOriginal
      );
    },
    // whether localCivilizations has been modified and is != localCivilizationsOriginal
    localCivilizationChanged() {
      return this.civilizationArrayHasChanged(
        this.localCivilizations,
        this.localCivilizationsOriginal
      );
    },
    // whether any value in global/local civilizations is invalid
    civilizationValueError() {
      return (
        this.globalCivilizations.some(elem => elem.error) ||
        this.localCivilizations.some(elem => elem.error)
      );
    }
  },
  watch: {
    async dialogToggle(newVal, oldVal) {
      if (!oldVal && newVal) {
        // load data on dialog init
        let globalCivilizationsLoading = this.getGlobalCivilizations();
        let localCivilizationsLoading = this.getLocalCivilizations();
        await Promise.all([
          globalCivilizationsLoading,
          localCivilizationsLoading
        ]);
        this.loaded = true;
      }
      if (oldVal && !newVal) {
        // reset data on dialog destroy
        this.globalCivilizations = [];
        this.globalCivilizationsOriginal = [];
        this.localCivilizations = [];
        this.localCivilizationsOriginal = [];
        this.changesSaved = false;
        this.savingError = null;
      }
    }
  },
  methods: {
    // retrieve globalCivilization data
    getGlobalCivilizations() {
      var self = this;
      return $api.get(
        `${window.actionUrl}globalCivilization?updateId=${self.selectedUpdate.updateId}&client_id=${self.user.clientId}`
      ).then(function(res) {
        self.setGlobalCivilizations(res);
      });
    },
    // retrieve localCivilization data
    getLocalCivilizations() {
      var self = this;
      return $api.get(
        `${window.actionUrl}localCivilization?updateId=${self.selectedUpdate.updateId}&client_id=${self.user.clientId}`
      ).then(function(res) {
        self.setLocalCivilizations(res);
      });
    },
    setGlobalCivilizations(res) {
      this.globalCivilizations = res.data.globalCivilizations.map(item =>
        Object.assign(item, {
          eCarFraction: item.eCarFraction.toString(),
          error: false
        })
      );
      this.globalCivilizationsOriginal = JSON.parse(
        JSON.stringify(this.globalCivilizations)
      );
    },
    setLocalCivilizations(res) {
      this.localCivilizations = res.data.localCivilizations.map(item =>
        Object.assign(item, {
          numberOfPersons: item.numberOfPersons.toString(),
          numberOfVehicles: item.numberOfVehicles.toString(),
          numberOfDCVehicles: item.numberOfDCVehicles.toString(),
          error: false
        })
      );
      this.localCivilizationsOriginal = JSON.parse(
        JSON.stringify(this.localCivilizations)
      );
    },
    // reset all update values to original state
    resetUpdateValues() {
      this.resetGlobalCivilizationValues();
      this.resetLocalCivilizationValues();
    },
    resetGlobalCivilizationValues() {
      this.globalCivilizations = JSON.parse(
        JSON.stringify(this.globalCivilizationsOriginal)
      );
    },
    resetLocalCivilizationValues() {
      this.localCivilizations = JSON.parse(
        JSON.stringify(this.localCivilizationsOriginal)
      );
    },
    civilizationArrayHasChanged(values, originalValues) {
      let currentValues = JSON.stringify(
        this.getCleanedCivilizationArray(values)
      );
      return originalValues && currentValues != JSON.stringify(originalValues);
    },
    getCleanedCivilizationArray(values) {
      // create copy of value array...
      return JSON.parse(JSON.stringify(values)).reduce((acc, elem) => {
        // ...without the attributes that are added by child component
        delete elem.editing;
        delete elem.changed;
        acc.push(elem);
        return acc;
      }, []);
    },
    async putCivilizationInput() {
      var requests = [];
      this.saving = true;
      this.savingError = null;
      if (this.globalCivilizationChanged) {
        let globalCivData = this.getCleanedCivilizationArray(
          this.globalCivilizations
        );
        let globalCivRequest = $api
          .put(
            `globalCivilization?updateId=${this.selectedUpdate.updateId}&client_id=${self.user.clientId}`,
            globalCivData
          )
          .then(res => {
            this.setGlobalCivilizations(res);
          })
          .catch(error => {
            if (this.savingError == null) {
              this.savingError = `globale Variablen: ${error.response.data.message}`;
            } else {
              this.savingError += `; globale Variablen: ${error.response.data.message}`;
            }
          });
        requests.push(globalCivRequest);
      }
      if (this.localCivilizationChanged) {
        let localCivData = this.getCleanedCivilizationArray(
          this.localCivilizations
        );
        let localCivRequest = $api
          .put(
            `localCivilization?updateId=${this.selectedUpdate.updateId}&client_id=${this.user.clientId}`,
            localCivData
          )
          .then(res => {
            this.setLocalCivilizations(res);
          })
          .catch(error => {
            if (this.savingError == null) {
              this.savingError = `lokale Variablen: ${error.response.data.message}`;
            } else {
              this.savingError += `; lokale Variablen: ${error.response.data.message}`;
            }
          });
        requests.push(localCivRequest);
      }
      await Promise.all(requests);
      this.saving = false;
      this.changesSaved = true;
    }
  }
};
</script>
